import { useState, useEffect } from 'react';
import { api } from '@/lib/api-client';
import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const getIcon = (status) => {
  if (status === 'operational') return <CheckCircle size={16} className="text-emerald-400" />;
  if (status === 'degraded') return <AlertTriangle size={16} className="text-yellow-500" />;
  return <XCircle size={16} className="text-red-500" />;
};

export default function Status() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.getStatusServices().then(setServices); }, []);

  const allOk = services.every(s => s.status === 'operational');

  return (
    <div className="bg-[#050505] min-h-screen pt-24">
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 data-testid="status-title" className="text-4xl sm:text-5xl font-bold text-white mb-4 section-marker">System Status</h1>

          <div data-testid="status-banner" className={`rounded-sm p-6 mb-8 border ${allOk ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-yellow-500/5 border-yellow-500/20'}`}>
            <div className="flex items-center gap-3">
              {allOk ? <CheckCircle size={24} className="text-emerald-400" /> : <AlertTriangle size={24} className="text-yellow-500" />}
              <div>
                <p className="font-mono font-bold text-white">{allOk ? 'All Systems Operational' : 'Partial Degradation'}</p>
                <p className="text-xs text-zinc-500 font-mono">Last updated: {new Date().toISOString().slice(0, 16)}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {services.map((svc, i) => (
              <div key={i} data-testid={`status-service-${i}`} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getIcon(svc.status)}
                  <span className="text-sm font-mono text-white">{svc.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs font-mono text-zinc-500">{svc.uptime}% uptime</span>
                  <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${svc.status === 'operational' ? 'bg-emerald-500/10 text-emerald-400' : svc.status === 'degraded' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'}`}>{svc.status}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <h2 className="text-lg font-mono text-white font-bold mb-4">Recent Incidents</h2>
            <div className="space-y-3">
              {[
                { date: '2026-02-18', title: 'Monitoring service degradation', status: 'Resolved', desc: 'Brief latency in metrics collection. No data loss.' },
                { date: '2026-02-10', title: 'Scheduled maintenance', status: 'Completed', desc: 'Infrastructure upgrades across all regions.' },
              ].map((inc, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-mono text-white">{inc.title}</h3>
                    <span className="text-xs font-mono text-emerald-400">{inc.status}</span>
                  </div>
                  <p className="text-xs text-zinc-500">{inc.desc}</p>
                  <p className="text-xs font-mono text-zinc-600 mt-2">{inc.date}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
